import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import useIsMobile from '../../lib/useIsMobile';
import useSEO from '../../lib/useSEO';
import MarketingNav from './MarketingNav';
import Footer from './Footer';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  green: '#16A34A',
  muted: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
};

export default function PartnerLearnPage() {
  const isMobile = useIsMobile();
  useSEO({ title: 'Give Your Clients a $30K Tax Win | ClearSeg Partners', description: 'ClearSeg handles cost segregation for your clients rental properties. You make the intro. We do the work. They save $30K+.', canonical: 'https://clearseg.com/partners/learn' });

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post('/affiliates/interest', { email, name });
    } catch {}
    setSubmitted(true);
    setSubmitting(false);
  };

  const formCard = (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: isMobile ? 24 : 28 }}>
      {submitted ? (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <div style={{ fontSize: 36, marginBottom: 10, color: C.green }}>&#10003;</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.navy }}>Check your email!</h3>
          <p style={{ fontSize: 14, color: C.muted, margin: '0 0 12px' }}>Full program details + your next steps are in your inbox.</p>
          <Link to="/partners" style={{ color: C.accent, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Ready now? Apply here &rarr;</Link>
        </div>
      ) : (
        <>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 4px', color: C.navy }}>See how it works</h3>
          <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px' }}>We'll send you the full breakdown + how to get your own co-branded page.</p>
          <form onSubmit={handleSubmit}>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
              style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 15, color: C.navy, background: C.bg, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }}
              onFocus={e => { e.target.style.borderColor = C.accent; }} onBlur={e => { e.target.style.borderColor = C.border; }}
            />
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Work email" required
              style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 15, color: C.navy, background: C.bg, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
              onFocus={e => { e.target.style.borderColor = C.accent; }} onBlur={e => { e.target.style.borderColor = C.border; }}
            />
            <button type="submit" disabled={submitting || !email} style={{ width: '100%', padding: '14px', borderRadius: 8, border: 'none', background: C.navy, color: '#FFFFFF', fontSize: 16, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', opacity: submitting || !email ? 0.6 : 1 }}>
              {submitting ? 'Sending...' : 'Send Me the Details'}
            </button>
          </form>
          <p style={{ fontSize: 11, color: C.light, marginTop: 8, textAlign: 'center' }}>Free. No commitment. Takes 2 minutes.</p>
        </>
      )}
    </div>
  );

  return (
    <div style={{ background: C.bg, color: C.navy, minHeight: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <MarketingNav />

      <section style={{ padding: isMobile ? '40px 20px 32px' : '64px 24px 48px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 28 : 48, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: isMobile ? 26 : 38, fontWeight: 900, lineHeight: 1.15, margin: '0 0 14px', letterSpacing: '-0.02em' }}>
              Give your clients a <span style={{ color: C.green }}>$30,000 tax win</span>. Earn $100 per study.
            </h1>
            <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, lineHeight: 1.6, margin: 0 }}>
              ClearSeg handles cost segregation studies for your clients' rental properties. You make the introduction. We do all the work. They save $30,000+ on their income taxes.
            </p>
          </div>
          <div style={{ width: isMobile ? '100%' : 380, flexShrink: 0 }}>
            {formCard}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '40px 20px' : '56px 24px', borderTop: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, textAlign: 'center', marginBottom: 12 }}>What Your Clients Get</h2>
          <p style={{ fontSize: 15, color: C.muted, textAlign: 'center', marginBottom: 36 }}>You recommend it. We deliver it. They love you for it.</p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            {[
              { title: 'PE-certified cost seg study', desc: 'Every classification reviewed and signed by a licensed Professional Engineer. IRS audit-ready.' },
              { title: '$799 per study', desc: '1,200+ studies completed. Your clients get the same analysis that large firms charge $5,000+ for.' },
              { title: '$30,000+ in deductions', desc: 'Average first-year accelerated depreciation on a single-family rental. Real money on their tax return.' },
              { title: 'Delivered in 2 weeks', desc: 'Study goes straight to their CPA or their inbox. No back-and-forth. No delays.' },
            ].map((item, i) => (
              <div key={i} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 6 }}>{item.title}</div>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '40px 20px' : '56px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, textAlign: 'center', marginBottom: 12 }}>What You Get</h2>
          <p style={{ fontSize: 15, color: C.muted, textAlign: 'center', marginBottom: 36 }}>Zero work. Real value for your clients. And yes, you get paid.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { title: 'Your own co-branded landing page', desc: 'Your logo, your name, your link. Clients see your brand \u2014 we power the backend.' },
              { title: 'One link to share', desc: 'Send your clients one URL. The referral is tracked automatically. No codes, no friction.' },
              { title: 'We handle everything', desc: 'Engineering analysis, PE certification, delivery to CPA. You make the intro, we do the rest.' },
              { title: '$100 per completed study', desc: 'No cap. No minimum. Refer one client or a hundred.' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                  background: '#F0FDF4', border: '1px solid #BBF7D0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: C.green, fontSize: 14, fontWeight: 700,
                }}>&#10003;</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 4 }}>{item.title}</div>
                  <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '40px 20px' : '56px 24px', borderTop: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, textAlign: 'center', marginBottom: 24 }}>The Math on One Referral</h2>
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: isMobile ? 24 : 28 }}>
            {[
              ['Client\'s rental property', '$400,000'],
              ['Reclassified amount', '$100,000'],
              ['Their first-year tax savings', '$37,000'],
            ].map(([label, val], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
                <span style={{ fontSize: 15, color: C.muted }}>{label}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: i === 2 ? C.green : C.navy }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 15, color: C.muted }}>Study cost</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>$799</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
              <span style={{ fontSize: 15, color: C.muted }}>Your referral fee</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.green }}>$100</span>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12, padding: '12px', background: '#F0FDF4', borderRadius: 8 }}>
              <span style={{ fontSize: 14, color: C.green, fontWeight: 600 }}>Your client saves $37,000. You earn $100. Everyone wins.</span>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '48px 20px' : '64px 24px', background: C.navy }}>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
          {submitted ? (
            <>
              <div style={{ fontSize: 36, marginBottom: 12, color: C.green }}>&#10003;</div>
              <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: '#FFFFFF' }}>Details sent!</h2>
              <p style={{ fontSize: 15, color: '#94A3B8', marginBottom: 16 }}>Check your inbox. We sent everything you need.</p>
              <Link to="/partners" style={{ color: '#FFFFFF', fontSize: 15, fontWeight: 600, textDecoration: 'underline' }}>Apply now &rarr;</Link>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, marginBottom: 8, color: '#FFFFFF' }}>Be the CPA who saves their client $30,000.</h2>
              <p style={{ fontSize: 15, color: '#94A3B8', marginBottom: 24 }}>It takes 2 minutes to set up. We handle the rest.</p>
              {formCard}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
